// General
import React, { useState, useContext, useRef } from "react";
// Contexts
import { PersonelContext } from "../data/personel/PersonelContext";
// Core / Table
import TablePage from "../core/TablePage";
// Modals
import PersonelAddModal from "../modals/personel/PersonelAddModal";
import PersonelUpdateModal from "../modals/personel/PersonelUpdateModal";
// UI / PrimeReact
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
// Icons
import { FaTrash, FaPencilAlt } from "react-icons/fa";
// General Data / Utility
import { useData } from "../data/general/DataContext";

const PersonelPage = () => {
  const { personelList, addPersonel, updatePersonel, deletePersonel } = useContext(PersonelContext);
  const { windowMode } = useData();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedPersonel, setSelectedPersonel] = useState(null);

  // 🔹 OverlayPanel tabanlı Action Menu (masaüstü)
  const ActionMenu = ({ rowData }) => {
    const op = useRef(null);
    if (windowMode === "mobile") return null;

    return (
      <>
        <Button
          label=". . ."
          className="p-button-rounded p-button-text"
          style={{ minWidth: "40px", padding: "8px 8px 12px", fontSize: "22px", fontWeight: "bold" }}
          onClick={(e) => op.current.toggle(e)}
        />
        <OverlayPanel ref={op} dismissable showCloseIcon={false} style={{ padding: "0.5rem", left: "-5px" }}>
          <div className="flex flex-col gap-0 p-2 w-32">
            {/* Düzenle */}
            <Button
              icon={<FaPencilAlt />}
              label="Düzenle"
              className="p-button-text p-button-lg w-full justify-start"
              style={{ fontSize: "14px", gap: "10px" }}
              onClick={() => {
                setSelectedPersonel(rowData);
                setShowUpdateModal(true);
                op.current.hide();
              }}
            />
            <div className="w-full border-t border-gray-300 my-1"></div>
            {/* Sil */}
            <Button
              icon={<FaTrash />}
              label="Sil"
              className="p-button-text p-button-lg w-full justify-start"
              style={{ color: "red", fontSize: "14px", gap: "10px" }}
              onClick={() => {
                deletePersonel(rowData.id);
                op.current.hide();
              }}
            />
          </div>
        </OverlayPanel>
      </>
    );
  };

  // 🔹 Kolonlar
  const columns = [
    { field: "id", header: "ID", hideInFilter: true },
    { field: "fullName", header: "Ad Soyad" },
    { field: "company", header: "Firma", hideInFilter: true },
    { field: "position", header: "Pozisyon" },
    {
      field: "address",
      header: "Adres",
      hideInFilter: true,
      body: (rowData) => (
        <div className="flex flex-col ">
          <span>{rowData.address}</span>
          <span className="text-gray-500 ">{rowData.city} / {rowData.district}</span>
        </div>
      ),
    },
    { field: "phone", header: "Telefon", hideInFilter: true },
    { field: "workType", header: "Mesai Durumu" },
    { field: "status", header: "Durum" },
    {
      header: "İşlemler",
      body: (rowData) => {
        if (windowMode === "mobile") {
          return (
            <div className="flex gap-2 mt-2 w-full">
              {/* Düzenle Butonu */}
              <Button
                icon={<FaPencilAlt />}
                label="Düzenle"
                className="flex-1"
                style={{
                  backgroundColor: "#3b82f6", 
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  height: "30px",
                  fontSize: "12px",
                  justifyContent: "center",
                  gap: "4px",
                  padding: "0 6px",
                }}
                onClick={() => {
                  setSelectedPersonel(rowData);
                  setShowUpdateModal(true);
                }}
              />
              {/* Sil Butonu */}
              <Button
                icon={<FaTrash />}
                label="Sil"
                className="flex-1"
                style={{
                  backgroundColor: "#ef4444", 
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  height: "30px",
                  fontSize: "12px",
                  justifyContent: "center",
                  gap: "4px",
                  padding: "0 6px",
                }}
                onClick={() => deletePersonel(rowData.id)}
              />
            </div>
          );
        } else {
          return <ActionMenu rowData={rowData} />;
        }
      },
      hideInFilter: true,
    },
  ];

  // 🔹 Kolon stilleri
  const enhancedColumns = columns.map((col) =>
    col.field === "id"
      ? { ...col, style: { width: "60px", padding: "0 12px" } }
      : { ...col, style: { padding: "0 12px" } }
  );

  // 🔹 Kolon filtreleme
  const columnFilterOptions = {
    fullName: { type: "text" },
    company: { type: "text" },
    position: { type: "text" },
    address: { type: "autocomplete", options: Array.from(new Set(personelList.map(p => p.address))).filter(Boolean) },
    phone: { type: "text" },
    workType: { type: "checkbox", options: Array.from(new Set(personelList.map(p => p.workType))).filter(Boolean) },
    status: { type: "checkbox", options: Array.from(new Set(personelList.map(p => p.status))).filter(Boolean) },
  };

  return (
    <>
      <div className="p-6">
        <TablePage
          title="Personel Listesi"
          subTitle="Personel ekleyebilir, düzenleyebilir veya silebilirsiniz."
          columns={enhancedColumns}
          data={personelList}
          defaultRows={10}
          rowsPerPageOptions={[10, 15, 25, 50,100]}
          showSearch
          onAdd={() => setShowAddModal(true)}
          columnFilterOptions={columnFilterOptions}
        />
      </div>

      <PersonelAddModal
        show={showAddModal}
        handleClose={() => setShowAddModal(false)}
        onSubmit={(personel) => addPersonel(personel)}
      />

      <PersonelUpdateModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        personel={selectedPersonel}
      />
    </>
  );
};

export default PersonelPage;
